import { useState } from "react";
import { Phone, PhoneOff, Mic, Loader2, TestTube } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { useCallMonitor } from "@/hooks/useCallMonitor";
import { useToast } from "@/hooks/use-toast";

interface CallMonitorPanelProps {
  jobId?: string;
  leadId?: string;
  mode?: 'job' | 'lead' | 'new-lead';
}

export function CallMonitorPanel({ jobId, leadId, mode = 'new-lead' }: CallMonitorPanelProps) {
  const { toast } = useToast();
  const {
    isActive,
    transcript,
    userTranscript,
    lastCallResults,
    startMonitoring,
    stopMonitoring,
  } = useCallMonitor();

  const [isProcessing, setIsProcessing] = useState(false);
  const [isTesting, setIsTesting] = useState(false);

  const handleStart = async () => {
    try {
      await startMonitoring();
      toast({
        title: "Call monitoring started",
        description: "Bridgette is now listening and taking notes",
      });
    } catch (error) {
      console.error('Failed to start monitoring:', error);
      toast({
        title: "Failed to start call",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    }
  };

  const handleStop = async () => {
    setIsProcessing(true);
    try {
      const results = await stopMonitoring({ jobId, leadId, mode });
      const notes = results?.notes ?? 0;
      const tasks = results?.tasks ?? 0;

      toast({
        title: "Call ended",
        description: `Created ${notes} notes and ${tasks} tasks`,
      });
    } catch (error) {
      console.error('Failed to stop monitoring:', error);
      toast({
        title: "Error ending call",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleTestConnection = async () => {
    setIsTesting(true);
    try {
      await startMonitoring();
      
      toast({
        title: "Test connection established",
        description: "Say 'Testing one two three, this is Mike Johnson' to verify English transcription",
      });

      // Auto-stop after 10 seconds for test
      setTimeout(async () => {
        if (isActive) {
          await stopMonitoring({ mode: 'new-lead' });
          setIsTesting(false);
        }
      }, 10000);
    } catch (error) {
      console.error('Test failed:', error);
      toast({
        title: "Test connection failed",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
      setIsTesting(false);
    }
  };

  return (
    <Card className="p-4 border-primary/20 bg-gradient-to-br from-primary/5 to-secondary/5 shadow-sm">
      <div className="space-y-3">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              isActive ? 'bg-destructive animate-pulse' : 'bg-primary'
            }`}>
              <Phone className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-semibold">Call with Bridgette</h3>
              <p className="text-xs text-muted-foreground">
                {isActive ? "Recording..." : "Start monitored call"}
              </p>
            </div>
          </div>
          
          {isActive && (
            <Badge variant="destructive" className="gap-1 h-5 text-xs px-2">
              <Mic className="w-2.5 h-2.5" />
              Live
            </Badge>
          )}
        </div>

        {/* Transcript Display */}
        {(isActive || transcript.length > 0) && (
          <ScrollArea className="h-48 rounded-md border bg-background/50 p-3">
            <div className="space-y-2">
              {transcript.map((msg, idx) => (
                <div
                  key={idx}
                  className={`text-xs ${
                    msg.role === 'assistant'
                      ? 'text-primary font-medium'
                      : 'text-foreground'
                  }`}
                >
                  <span className="font-semibold text-xs">
                    {msg.role === 'assistant' ? 'Bridgette' : 'You'}:
                  </span>{' '}
                  {msg.content}
                </div>
              ))}
              
              {userTranscript && (
                <div className="text-xs text-muted-foreground italic">
                  <span className="font-semibold">You (typing):</span> {userTranscript}
                </div>
              )}
            </div>
          </ScrollArea>
        )}

        {/* Results Display */}
        {lastCallResults && !isActive && (
          <div className="grid grid-cols-2 gap-3 p-3 rounded-md bg-success/10 border border-success/20">
            <div className="text-center">
              <div className="text-xl font-bold text-success">{lastCallResults.notes}</div>
              <div className="text-xs text-muted-foreground">Notes</div>
            </div>
            <div className="text-center">
              <div className="text-xl font-bold text-success">{lastCallResults.tasks}</div>
              <div className="text-xs text-muted-foreground">Tasks</div>
            </div>
          </div>
        )}

        {/* Active Call Warning */}
        {isActive && transcript.length > 0 && (
          <div className="p-2 rounded-md bg-warning/10 border border-warning/20">
            <p className="text-xs font-medium text-warning">
              ⚠️ Click "End Call" to save transcript ({transcript.length} messages)
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-2">
          {!isActive ? (
            <>
              <Button
                onClick={handleStart}
                className="flex-1"
                size="sm"
              >
                <Phone className="w-3.5 h-3.5 mr-1.5" />
                Start Call
              </Button>
              <Button
                onClick={handleTestConnection}
                variant="outline"
                size="sm"
                disabled={isTesting}
              >
                {isTesting ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                    Testing
                  </>
                ) : (
                  <>
                    <TestTube className="w-3.5 h-3.5 mr-1.5" />
                    Test
                  </>
                )}
              </Button>
            </>
          ) : (
            <Button
              onClick={handleStop}
              variant="destructive"
              className="flex-1"
              size="sm"
              disabled={isProcessing}
            >
              {isProcessing ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <PhoneOff className="w-3.5 h-3.5 mr-1.5" />
                  End Call
                </>
              )}
            </Button>
          )}
        </div>

        {/* Instructions */}
        {!isActive && transcript.length === 0 && (
          <div className="text-xs text-muted-foreground space-y-0.5 p-2 rounded-md bg-muted/30">
            <p>• Auto-captures customer details & action items</p>
            <p>• Creates notes & tasks in CRM after call</p>
          </div>
        )}
      </div>
    </Card>
  );
}

export default CallMonitorPanel;
